import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { SUPPORTED_LANGUAGES } from '../../i18n/index'
import { cn } from '../../lib/utils'
import { Globe } from 'lucide-react'
import {
  getLocaleFromPathname,
  resolvePublicLocale,
  stripLocalePrefix,
} from '../../lib/publicLocaleRouting'

export const LanguageSwitcher: React.FC = () => {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const navigate = useNavigate()

  const currentLang = useMemo(() => {
    const normalizedLanguage = (i18n.resolvedLanguage || i18n.language || 'pt').toLowerCase()
    return SUPPORTED_LANGUAGES.find(({ code }) => normalizedLanguage.startsWith(code)) ?? SUPPORTED_LANGUAGES[0]
  }, [i18n.language, i18n.resolvedLanguage])

  const currentLabel = t(currentLang.labelKey)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleChange = (code: string) => {
    const currentLocale = getLocaleFromPathname(location.pathname)
    if (currentLocale !== null) {
      const newLocale = resolvePublicLocale(code)
      const subPath = stripLocalePrefix(location.pathname)
      const newPath = `/${newLocale}${subPath === '/' ? '' : subPath}`
      navigate(`${newPath}${location.search}${location.hash}`)
    } else {
      void i18n.changeLanguage(code)
    }
    setOpen(false)
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 rounded-md border border-border bg-background/80 px-3 py-2 text-sm font-medium text-foreground shadow-sm backdrop-blur transition-colors hover:bg-surface focus:outline-none focus:ring-2 focus:ring-accent"
        title={t('ui.languageSwitcher.triggerTitle')}
        aria-label={t('ui.languageSwitcher.triggerLabel')}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Globe className="h-4 w-4 shrink-0 text-accent" />
        <span>{currentLabel}</span>
      </button>

      {open && (
        <div
          role="menu"
          aria-label={t('ui.languageSwitcher.triggerLabel')}
          className="absolute right-0 top-full z-50 mt-1.5 w-48 overflow-hidden rounded-lg border border-border bg-card py-1 shadow-precision-lg"
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <LanguageOption
              key={lang.code}
              code={lang.code}
              flag={lang.flag}
              label={t(lang.labelKey)}
              isActive={currentLang.code === lang.code}
              onSelect={handleChange}
            />
          ))}
        </div>
      )}
    </div>
  )
}

type LanguageOptionProps = {
  code: string
  flag: string
  label: string
  isActive: boolean
  onSelect: (code: string) => void
}

const LanguageOption: React.FC<LanguageOptionProps> = ({ code, flag, label, isActive, onSelect }) => {
  return (
    <button
      type="button"
      onClick={() => onSelect(code)}
      className={cn(
        'w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors text-left',
        isActive ? 'bg-accent/5 text-accent font-medium' : 'text-foreground hover:bg-surface'
      )}
      aria-current={isActive ? 'true' : undefined}
      role="menuitem"
    >
      <span className="text-base leading-none">{flag}</span>
      <span>{label}</span>
      {isActive && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-accent" />}
    </button>
  )
}
